import { RetryButton } from '@/shared/components/RetryButton';
import { fileReadMessage } from '../fileReadMessage';
import { useFileObjectUrl } from '../hooks/useFileObjectUrl';

/**
 * A PDF, in the browser's own viewer (2026-10-05). It used to be classed as
 * binary, so the pane offered only a download.
 *
 * Read as a blob for the reason `ImagePreview` gives: the content endpoint
 * needs a token a frame cannot send. The blob is typed `application/pdf`
 * whatever the server stored, because the browser picks its viewer from that
 * type. Paging, zoom, search and printing are the viewer's own; the download
 * stays in the preview header with every other file kind.
 */
export function PdfPreview({
  workspaceId,
  path,
  name,
}: {
  workspaceId: string;
  path: string;
  name: string;
}) {
  const { url, failed, failureReason, retry } = useFileObjectUrl(
    workspaceId,
    path,
    true,
    'application/pdf',
  );

  if (failed) {
    /*
      `failureReason`, not just `failed` (F17/FR-067). This pane used to discard
      the reason and tell a reader whose file had been deleted, and a reader
      who was never allowed to open it, the same thing: that the PDF "could not
      be loaded". The wording is shared with the HTML and image panes.
    */
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-4">
        <p className="text-muted-foreground text-sm">
          {fileReadMessage(failureReason, 'PDF')}
        </p>
        {/*
          A read that failed in transit is the one reason worth asking again
          about, and until now only the HTML pane offered the control: a reader
          whose PDF request dropped had the sentence and no way to retry, and
          the read does not retry itself (`retry: false`). The other three
          reasons are answers, not failures, so they get no button.
        */}
        {failureReason === 'failed' ? <RetryButton onRetry={retry} /> : null}
      </div>
    );
  }

  if (url === undefined) {
    return (
      <div className="flex min-h-0 flex-1 p-4">
        <div
          aria-hidden="true"
          className="bg-muted min-h-64 flex-1 animate-pulse rounded-lg"
        />
      </div>
    );
  }

  return (
    <iframe src={url} title={name} className="min-h-0 w-full flex-1 border-0" />
  );
}
